import { BufferAttribute, BufferGeometry, Vector3 } from 'three';
import { weldMeshVertices } from './alignerMeshShell';

/**
 * Fuses independently-segmented jaw parts (gum sheet + tooth crowns) into ONE
 * topologically connected sheet, so the hollow-shell SDF sees no seams at all.
 *
 * Segmentation cuts teeth out of the scan and correction keeps the gum's
 * socket rims snapped exactly ONTO tooth border vertices (they follow the
 * teeth rigidly), but the two rims have different vertex densities: a gum rim
 * edge is a straight chord across two or three vertices of the tooth's rim
 * path. Position-welding alone therefore leaves every seam as a T-junction —
 * the chord never pairs with the path edges, both stay OPEN borders, and the
 * shell field decorates the whole gum line and every interproximal seam with
 * free-rim artifacts (bead chains, flush-cut notches, pinhole tunnels).
 *
 * Three passes restore the original connectivity:
 *   1. WELD coincident vertices across parts,
 *   2. SPLIT every border edge at border vertices of the other side that lie
 *      on it (within `tolerance`) until no T-junctions are left,
 *   3. ZIP narrow slits: opposing border shores closer than `zipGapMm` are
 *      snapped to their midpoints and re-welded (rims that drifted slightly
 *      apart — ARAP relaxation, treatment movement below the gap limit) —
 *      then step 2 reruns for the T-junctions the zip exposed.
 *
 * Borders with no counterpart — the outer gum perimeter, interproximal
 * windows opened wider than `zipGapMm` by tooth movement, scan holes — are
 * left untouched. Returns a single indexed geometry (positions only) plus
 * stats; `sameDirectionPairs` counts edges whose faces traverse them in the
 * SAME direction (winding disagreement — doubled scan layers mostly).
 */
export type SeamFusionStats = {
  openBefore: number;
  openAfter: number;
  splits: number;
  zipped: number;
  passes: number;
  sameDirectionPairs: number;
};

const WELD_TOLERANCE = 1e-3;

export function fuseSeamTJunctions(
  parts: BufferGeometry[],
  tolerance = 0.25,
  maxPasses = 12,
  zipGapMm = 0.5,
): { geometry: BufferGeometry; stats: SeamFusionStats } {
  let geometry = weldMeshVertices(concatenateParts(parts), WELD_TOLERANCE);

  const stats: SeamFusionStats = {
    openBefore: countOpenEdges(geometry),
    openAfter: 0,
    splits: 0,
    zipped: 0,
    passes: 0,
    sameDirectionPairs: 0,
  };

  stats.splits += splitTJunctions(geometry, tolerance, maxPasses, stats);

  // Zip narrow slits, then re-split the T-junctions each zip exposes.
  for (let round = 0; round < 3 && zipGapMm > 0; round++) {
    const moved = snapOpposingShores(geometry, zipGapMm);
    if (moved === 0) break;
    stats.zipped += moved;
    geometry = weldMeshVertices(geometry, WELD_TOLERANCE);
    stats.splits += splitTJunctions(geometry, tolerance, maxPasses, stats);
  }

  stats.openAfter = countOpenEdges(geometry);
  stats.sameDirectionPairs = countSameDirectionPairs(geometry);
  return { geometry, stats };
}

export type ManifoldRepairStats = {
  duplicateFacesDropped: number;
  facesFlipped: number;
  flapsPeeled: number;
  sliversDropped: number;
  loopsFilled: number;
  rounds: number;
  /** Directed edges left without an opposite twin (0 on a closed 2-manifold). */
  openEdgesAfter: number;
  sameDirectionAfter: number;
};

/**
 * Takes the fused sheet the rest of the way to a 2-manifold, which is what a
 * DIRECT offset needs and the voxel route never did: an SDF is topology-blind,
 * so the fusion was free to leave doubled scan layers, overlapping slivers and
 * sub-millimetre slits behind — but buildOffsetShell bridges every unpaired
 * edge it sees, so each of those defects propagates into the shell as an open
 * edge and the slicer refuses the lot.
 *
 * Measured on the captured upper jaw, the defects come in four kinds, and
 * each gets its own pass, repeated until the census is clean:
 *   1. duplicate faces: the same vertex triple twice with the SAME winding is
 *      a doubled scan layer welded onto itself (keep one); with OPPOSITE
 *      winding it is a zero-thickness pocket (drop both).
 *   2. winding harmonisation. An island of faces wound backwards reads as a
 *      same-direction pair along its every rim edge, and DELETING there just
 *      eats good surface one ring per round — the island has to be FLIPPED.
 *      BFS across cleanly-shared edges, flipping faces into agreement;
 *      non-manifold edges are not walked, so a junction cannot propagate a
 *      wrong orientation.
 *   3. what still conflicts is genuine junk: faces with two or three
 *      same-direction edges (flaps lying flat on the surface) are peeled, and
 *      of a surviving lone pair one face is dropped — the ORIGINAL when a
 *      repair fan is involved (the fan is the fix, the original is the
 *      defect; dropping the fan just reopens the hole it closed and the next
 *      round refills it identically, forever), by area otherwise.
 *   4. boundary loops (unzipped slits, scan pinholes, the holes passes 1–3
 *      opened, and interproximal windows the teeth's movement opened wider
 *      than the zip reaches): filled with a CENTROID fan — one new vertex per
 *      loop — except the `keepLargestLoops` longest, the outer perimeter when
 *      the rim is left open on purpose. A first-vertex fan is cheaper but its
 *      triangles share long chords, and over the big non-planar interproximal
 *      windows those chords collide into fresh same-direction conflicts
 *      (measured: edges 14 mm long owned by three fan faces); a centroid fan
 *      is manifold by construction whatever the loop's shape. Filling a
 *      window is deliberate, not collateral: it is the same bridge the voxel
 *      route's interproximal blockout built, and the foil never pulls into
 *      those gaps anyway.
 */
export function repairToManifold(
  geometry: BufferGeometry,
  keepLargestLoops = 0,
  maxRounds = 6,
  /**
   * When set, loops whose PERIMETER reaches this are protected from filling —
   * the real rims (outer gum perimeter, a trimmed lingual edge, a skirt's
   * bottom ring). The same absolute number the skirt selects rims by, so the
   * two always agree on what stays open.
   */
  protectRimsOverMm?: number,
): ManifoldRepairStats {
  const stats: ManifoldRepairStats = {
    duplicateFacesDropped: 0,
    facesFlipped: 0,
    flapsPeeled: 0,
    sliversDropped: 0,
    loopsFilled: 0,
    rounds: 0,
    openEdgesAfter: 0,
    sameDirectionAfter: 0,
  };

  let position = geometry.getAttribute('position') as BufferAttribute;
  let synthetic = new Uint8Array(geometry.getIndex()!.count / 3);
  const corner = [new Vector3(), new Vector3(), new Vector3()];
  const ab = new Vector3();
  const ac = new Vector3();
  const faceArea = (tris: Uint32Array | number[], f: number): number => {
    for (let e = 0; e < 3; e++) corner[e].fromBufferAttribute(position, tris[f * 3 + e]);
    return ab.subVectors(corner[1], corner[0]).cross(ac.subVectors(corner[2], corner[0])).length();
  };

  for (let round = 0; round < maxRounds; round++) {
    stats.rounds = round + 1;
    const index = geometry.getIndex()!;
    const tris = index.array as Uint32Array;
    const faceCount = tris.length / 3;
    const vertexCount = position.count;
    const drop = new Uint8Array(faceCount);
    let dropped = 0;

    // Pass 1: degenerate + duplicate faces. Same winding = doubled layer,
    // keep one; opposite winding = a zero-thickness pocket, drop both.
    const seenTriple = new Map<string, number>();
    for (let f = 0; f < faceCount; f++) {
      const a = tris[f * 3];
      const b = tris[f * 3 + 1];
      const c = tris[f * 3 + 2];
      if (a === b || b === c || c === a) {
        drop[f] = 1;
        dropped++;
        continue;
      }
      const key = [a, b, c].sort((x, y) => x - y).join(',');
      const first = seenTriple.get(key);
      if (first === undefined) {
        seenTriple.set(key, f);
        continue;
      }
      drop[f] = 1;
      dropped++;
      stats.duplicateFacesDropped++;
      const fa = tris[first * 3];
      const fb = tris[first * 3 + 1];
      const sameWinding =
        (fa === a && fb === b) || (fa === b && fb === c) || (fa === c && fb === a);
      if (!sameWinding && !drop[first]) {
        drop[first] = 1;
        dropped++;
        stats.duplicateFacesDropped++;
      }
    }

    // Pass 2: winding harmonisation. Components are built over CONSISTENT
    // edges only — two owners traversing the edge in opposite directions — so
    // a backwards island isolates itself (its whole rim is same-direction
    // conflicts) and a junction cannot smuggle a wrong orientation across
    // (walking across the conflicts themselves is what once flipped half the
    // jaw here). Then, largest component first, each component is flipped
    // when the majority of its interface edges with already-anchored
    // components disagree.
    {
      const owner1 = new Map<number, number>();
      const owner2 = new Map<number, number>();
      const overfull = new Set<number>();
      for (let f = 0; f < faceCount; f++) {
        if (drop[f]) continue;
        for (let e = 0; e < 3; e++) {
          const a = tris[f * 3 + e];
          const b = tris[f * 3 + ((e + 1) % 3)];
          const key = a < b ? a * vertexCount + b : b * vertexCount + a;
          if (overfull.has(key)) continue;
          if (!owner1.has(key)) owner1.set(key, f);
          else if (!owner2.has(key)) owner2.set(key, f);
          else {
            overfull.add(key);
            owner1.delete(key);
            owner2.delete(key);
          }
        }
      }
      // +1 when face f walks {a,b} as a→b, −1 as b→a, 0 when absent.
      const traverses = (f: number, a: number, b: number): number => {
        for (let e = 0; e < 3; e++) {
          const from = tris[f * 3 + e];
          const to = tris[f * 3 + ((e + 1) % 3)];
          if (from === a && to === b) return 1;
          if (from === b && to === a) return -1;
        }
        return 0;
      };

      const component = new Int32Array(faceCount).fill(-1);
      const areas: number[] = [];
      const members: number[][] = [];
      const queue: number[] = [];
      for (let seed = 0; seed < faceCount; seed++) {
        if (drop[seed] || component[seed] >= 0) continue;
        const id = areas.length;
        component[seed] = id;
        queue.length = 0;
        queue.push(seed);
        let area = 0;
        const list: number[] = [];
        while (queue.length > 0) {
          const f = queue.pop()!;
          list.push(f);
          area += faceArea(tris, f);
          for (let e = 0; e < 3; e++) {
            const a = tris[f * 3 + e];
            const b = tris[f * 3 + ((e + 1) % 3)];
            const key = a < b ? a * vertexCount + b : b * vertexCount + a;
            const g = owner1.get(key) === f ? owner2.get(key) : owner1.get(key);
            if (g === undefined || drop[g] || component[g] >= 0) continue;
            if (traverses(f, a, b) === traverses(g, a, b)) continue;
            component[g] = id;
            queue.push(g);
          }
        }
        areas.push(area);
        members.push(list);
      }

      if (areas.length > 1) {
        // Interface census between components: for every shared edge, do the
        // two sides agree? (Conflicted edges are exactly the ones that were
        // not walked, so they only ever show up here.)
        const seenPair = new Map<number, { same: number; opposite: number }>();
        const pairKey = (p: number, q: number) => (p < q ? p * areas.length + q : q * areas.length + p);
        const edgeFaces = new Map<number, number[]>();
        for (let f = 0; f < faceCount; f++) {
          if (drop[f]) continue;
          for (let e = 0; e < 3; e++) {
            const a = tris[f * 3 + e];
            const b = tris[f * 3 + ((e + 1) % 3)];
            const key = a < b ? a * vertexCount + b : b * vertexCount + a;
            if (!edgeFaces.has(key)) edgeFaces.set(key, []);
            edgeFaces.get(key)!.push(f);
          }
        }
        edgeFaces.forEach((faces, key) => {
          if (faces.length !== 2) return;
          const [f, g] = faces;
          if (component[f] === component[g]) return;
          const a = Math.floor(key / vertexCount);
          const b = key - a * vertexCount;
          const pk = pairKey(component[f], component[g]);
          if (!seenPair.has(pk)) seenPair.set(pk, { same: 0, opposite: 0 });
          const tally = seenPair.get(pk)!;
          if (traverses(f, a, b) === traverses(g, a, b)) tally.same++;
          else tally.opposite++;
        });

        // Anchor the largest, then decide the rest in size order against
        // whatever is already anchored.
        const order = areas.map((_a, id) => id).sort((p, q) => areas[q] - areas[p]);
        const state = new Int8Array(areas.length); // 0 pending, 1 kept, -1 flipped
        state[order[0]] = 1;
        for (let i = 1; i < order.length; i++) {
          const id = order[i];
          let same = 0;
          let opposite = 0;
          for (let j = 0; j < i; j++) {
            const anchored = order[j];
            const tally = seenPair.get(pairKey(id, anchored));
            if (!tally) continue;
            // A flipped anchor reverses what its census rows mean.
            const flippedAnchor = state[anchored] === -1;
            same += flippedAnchor ? tally.opposite : tally.same;
            opposite += flippedAnchor ? tally.same : tally.opposite;
          }
          state[id] = same > opposite ? -1 : 1;
          if (state[id] === -1) {
            for (const f of members[id]) {
              const tmp = tris[f * 3 + 1];
              tris[f * 3 + 1] = tris[f * 3 + 2];
              tris[f * 3 + 2] = tmp;
              stats.facesFlipped++;
            }
          }
        }
      }
    }

    // Pass 3a: flaps — faces with 2+ same-direction edges. Iterated, because
    // every removal changes the census of its neighbours.
    for (let peel = 0; peel < 16; peel++) {
      const directed = new Map<number, number>();
      for (let f = 0; f < faceCount; f++) {
        if (drop[f]) continue;
        for (let e = 0; e < 3; e++) {
          const key = tris[f * 3 + e] * vertexCount + tris[f * 3 + ((e + 1) % 3)];
          directed.set(key, (directed.get(key) ?? 0) + 1);
        }
      }
      let peeled = 0;
      for (let f = 0; f < faceCount; f++) {
        if (drop[f]) continue;
        let conflicts = 0;
        for (let e = 0; e < 3; e++) {
          const key = tris[f * 3 + e] * vertexCount + tris[f * 3 + ((e + 1) % 3)];
          if ((directed.get(key) ?? 0) > 1) conflicts++;
        }
        if (conflicts < 2) continue;
        drop[f] = 1;
        dropped++;
        peeled++;
        stats.flapsPeeled++;
        // Its edges leave the census immediately, or the pass peels both
        // layers of a doubled region where removing one was enough.
        for (let e = 0; e < 3; e++) {
          const key = tris[f * 3 + e] * vertexCount + tris[f * 3 + ((e + 1) % 3)];
          directed.set(key, (directed.get(key) ?? 1) - 1);
        }
      }
      if (peeled === 0) break;
    }

    // Pass 3b: fins — a lone same-direction pair between two otherwise-sane
    // faces is two sheets crossing; one of them goes. A repair fan always
    // survives over an original: the fan is the fix and the original is the
    // defect, and dropping the fan only reopens the hole for the next round
    // to refill identically — the ping-pong that once kept a steady two dozen
    // conflicts alive here. Otherwise the smaller face is the sliver.
    {
      const owners = new Map<number, number[]>();
      for (let f = 0; f < faceCount; f++) {
        if (drop[f]) continue;
        for (let e = 0; e < 3; e++) {
          const key = tris[f * 3 + e] * vertexCount + tris[f * 3 + ((e + 1) % 3)];
          if (!owners.has(key)) owners.set(key, []);
          owners.get(key)!.push(f);
        }
      }
      owners.forEach((faces) => {
        if (faces.length < 2) return;
        const alive = faces.filter((f) => !drop[f]);
        if (alive.length < 2) return;
        // The keeper sorts last: synthetic beats original, bigger beats
        // smaller within a kind.
        alive.sort(
          (f, g) => synthetic[f] - synthetic[g] || faceArea(tris, f) - faceArea(tris, g),
        );
        for (let i = 0; i < alive.length - 1; i++) {
          if (drop[alive[i]]) continue;
          drop[alive[i]] = 1;
          dropped++;
          stats.sliversDropped++;
        }
      });
    }

    if (dropped > 0) {
      const next = new Uint32Array((faceCount - dropped) * 3);
      const nextSynthetic = new Uint8Array(faceCount - dropped);
      let cursor = 0;
      for (let f = 0; f < faceCount; f++) {
        if (drop[f]) continue;
        nextSynthetic[cursor / 3] = synthetic[f];
        next[cursor++] = tris[f * 3];
        next[cursor++] = tris[f * 3 + 1];
        next[cursor++] = tris[f * 3 + 2];
      }
      geometry.setIndex(new BufferAttribute(next, 1));
      synthetic = nextSynthetic;
    }

    // Pass 4: fill what is open — the defects' holes and the genuine slits.
    const filled = fillLoopsWithCentroidFans(geometry, keepLargestLoops, protectRimsOverMm);
    stats.loopsFilled += filled.loops;
    if (filled.faces > 0) {
      const grown = new Uint8Array(geometry.getIndex()!.count / 3);
      grown.set(synthetic);
      grown.fill(1, synthetic.length);
      synthetic = grown;
      position = geometry.getAttribute('position') as BufferAttribute;
    }

    const open = countUnpairedDirected(geometry);
    stats.openEdgesAfter = Math.max(0, open.unpaired - filled.protectedEdges);
    stats.sameDirectionAfter = open.sameDirection;
    if (stats.openEdgesAfter === 0 && open.sameDirection === 0) break;
  }
  return stats;
}

/**
 * Fills every closed boundary loop except the `keepLargest` longest with a
 * fan around ONE NEW vertex at the loop's centroid. Unlike a first-vertex
 * fan, the centroid fan shares no chords between its triangles — every
 * interior edge is centroid↔rim, used exactly twice in opposite directions —
 * so it is manifold whatever the loop's shape. Open chains (defect walks that
 * never close) are left for the calling rounds to peel further.
 */
function fillLoopsWithCentroidFans(
  geometry: BufferGeometry,
  keepLargest: number,
  protectRimsOverMm?: number,
): { loops: number; faces: number; protectedEdges: number } {
  const index = geometry.getIndex()!;
  const position = geometry.getAttribute('position') as BufferAttribute;
  const vertexCount = position.count;

  const directed = new Map<number, number>();
  for (let i = 0; i < index.count; i += 3) {
    for (let e = 0; e < 3; e++) {
      const key = index.getX(i + e) * vertexCount + index.getX(i + ((e + 1) % 3));
      directed.set(key, (directed.get(key) ?? 0) + 1);
    }
  }
  // Strictly open directed edges, a→[b…] (a junction vertex can have several).
  const openOut = new Map<number, number[]>();
  directed.forEach((count, key) => {
    const a = Math.floor(key / vertexCount);
    const b = key - a * vertexCount;
    if (count !== 1 || (directed.get(b * vertexCount + a) ?? 0) !== 0) return;
    if (!openOut.has(a)) openOut.set(a, []);
    openOut.get(a)!.push(b);
  });

  const usedEdge = new Set<number>();
  const loops: number[][] = [];
  openOut.forEach((tos, from) => {
    for (const to of tos) {
      if (usedEdge.has(from * vertexCount + to)) continue;
      const walk = [from];
      let at = from;
      let next = to;
      let closed = false;
      for (let guard = 0; guard < 100000; guard++) {
        usedEdge.add(at * vertexCount + next);
        if (next === from) {
          closed = true;
          break;
        }
        walk.push(next);
        const outs = (openOut.get(next) ?? []).filter(
          (o) => !usedEdge.has(next * vertexCount + o),
        );
        if (outs.length === 0) break;
        at = next;
        next = outs[0];
      }
      if (closed && walk.length >= 3) loops.push(walk);
    }
  });

  const perimeterOf = (walk: number[]): number => {
    let length = 0;
    for (let i = 0; i < walk.length; i++) {
      const a = walk[i] * 3;
      const b = walk[(i + 1) % walk.length] * 3;
      const array = position.array as Float32Array;
      length += Math.hypot(array[a] - array[b], array[a + 1] - array[b + 1], array[a + 2] - array[b + 2]);
    }
    return length;
  };
  const measured = loops
    .map((walk) => ({ walk, perimeter: perimeterOf(walk) }))
    .sort((p, q) => q.perimeter - p.perimeter);
  // Absolute threshold, matching the skirt's rim selection exactly — a
  // relative rule here once disagreed with the skirt about a trim-fragmented
  // rim's middle-sized loops, and the disagreement shipped as open edges.
  const isProtected = (i: number): boolean => {
    if (i < keepLargest) return true;
    if (protectRimsOverMm === undefined) return false;
    return measured[i].perimeter >= protectRimsOverMm;
  };
  let protectedEdges = 0;
  const fill: number[][] = [];
  for (let i = 0; i < measured.length; i++) {
    if (isProtected(i)) protectedEdges += measured[i].walk.length;
    else fill.push(measured[i].walk);
  }
  if (fill.length === 0) return { loops: 0, faces: 0, protectedEdges };

  const oldPositions = position.array as Float32Array;
  // A loop small enough gets SEWN — minimal-area triangulation over its own
  // vertices, no new vertex. This is what an open gum-tooth seam needs: on a
  // jaw whose gum rims were not snapped onto the crowns, the fusion leaves a
  // long thin slit around every tooth, and a centroid fan puts a vertex in
  // the middle of the slit with sliver triangles reaching half way around the
  // crown — measured, ten thousand tapered wall vertices downstream. Sewing
  // connects the two shores the way a stitch would. Bigger loops (the wide
  // interproximal windows) keep the centroid fan: the DP is O(n³) and a fan
  // over a roomy window is fine.
  // A chord of the sewing must not coincide with an edge the mesh already
  // has — the shores of a thin slit are usually connected SOMEWHERE, and a
  // duplicated edge is a same-direction pair the fin pass then deletes,
  // reopening the hole for an identical resew, forever. The DP steers around
  // existing edges; a loop it cannot sew under that rule falls back to a fan.
  const existingEdges = new Set<number>();
  for (let i = 0; i < index.count; i += 3) {
    for (let e = 0; e < 3; e++) {
      const a = index.getX(i + e);
      const b = index.getX(i + ((e + 1) % 3));
      existingEdges.add(a < b ? a * vertexCount + b : b * vertexCount + a);
    }
  }

  const sewn: number[][] = [];
  const fanned: number[][] = [];
  for (const loop of fill) (loop.length <= SEW_LIMIT ? sewn : fanned).push(loop);

  const sewnTriangles: number[][] = [];
  for (let i = sewn.length - 1; i >= 0; i--) {
    const triangles = sewLoopMinimalArea(sewn[i], oldPositions, existingEdges, vertexCount);
    if (triangles === null) {
      fanned.push(sewn[i]);
      sewn.splice(i, 1);
    } else {
      sewnTriangles.unshift(triangles);
      // The new chords are edges now too — the next loop must not reuse them.
      for (let t = 0; t < triangles.length; t += 3) {
        for (let e = 0; e < 3; e++) {
          const a = triangles[t + e];
          const b = triangles[t + ((e + 1) % 3)];
          existingEdges.add(a < b ? a * vertexCount + b : b * vertexCount + a);
        }
      }
    }
  }

  let faces = 0;
  for (const triangles of sewnTriangles) faces += triangles.length / 3;
  for (const loop of fanned) faces += loop.length;
  const grownPositions = new Float32Array((vertexCount + fanned.length) * 3);
  grownPositions.set(oldPositions);
  const oldIndex = index.array as Uint32Array;
  const grownIndex = new Uint32Array(oldIndex.length + faces * 3);
  grownIndex.set(oldIndex);

  let vertexCursor = vertexCount;
  let indexCursor = oldIndex.length;
  for (const triangles of sewnTriangles) {
    for (const corner of triangles) grownIndex[indexCursor++] = corner;
  }
  for (const loop of fanned) {
    let cx = 0;
    let cy = 0;
    let cz = 0;
    for (const v of loop) {
      cx += oldPositions[v * 3];
      cy += oldPositions[v * 3 + 1];
      cz += oldPositions[v * 3 + 2];
    }
    const centroid = vertexCursor++;
    grownPositions[centroid * 3] = cx / loop.length;
    grownPositions[centroid * 3 + 1] = cy / loop.length;
    grownPositions[centroid * 3 + 2] = cz / loop.length;
    // The open edge runs a→b with no partner; the fan triangle (b, a, c)
    // provides b→a and the interior edges pair with the neighbouring fans.
    for (let i = 0; i < loop.length; i++) {
      const a = loop[i];
      const b = loop[(i + 1) % loop.length];
      grownIndex[indexCursor++] = b;
      grownIndex[indexCursor++] = a;
      grownIndex[indexCursor++] = centroid;
    }
  }
  geometry.setAttribute('position', new BufferAttribute(grownPositions, 3));
  geometry.setIndex(new BufferAttribute(grownIndex.subarray(0, indexCursor), 1));
  return { loops: fill.length, faces, protectedEdges };
}

/** Loops up to this many edges are sewn by minimal-area DP; larger get a fan. */
const SEW_LIMIT = 200;

/**
 * Classic minimal-area polygon triangulation (Klincsek DP): cost(i,j) = the
 * cheapest triangulation of the loop stretch i..j, built up by stretch
 * length. O(n³), bounded by SEW_LIMIT. Triangles come out wound AGAINST the
 * loop's walk direction, so each boundary edge a→b gets its b→a partner and
 * every internal chord is used exactly twice in opposite directions —
 * manifold by construction. Chords the mesh already owns (and zero-length
 * ones — a walk may revisit a vertex at a junction) are priced at Infinity;
 * returns null when no triangulation avoids them, and the caller falls back
 * to a centroid fan, whose edges all run to a fresh vertex and cannot
 * collide with anything.
 */
function sewLoopMinimalArea(
  loop: number[],
  positions: Float32Array,
  existingEdges: Set<number>,
  vertexCount: number,
): number[] | null {
  const n = loop.length;
  const ax = new Float64Array(n);
  const ay = new Float64Array(n);
  const az = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    ax[i] = positions[loop[i] * 3];
    ay[i] = positions[loop[i] * 3 + 1];
    az[i] = positions[loop[i] * 3 + 2];
  }
  const areaOf = (i: number, k: number, j: number): number => {
    const ux = ax[k] - ax[i];
    const uy = ay[k] - ay[i];
    const uz = az[k] - az[i];
    const vx = ax[j] - ax[i];
    const vy = ay[j] - ay[i];
    const vz = az[j] - az[i];
    const cx = uy * vz - uz * vy;
    const cy = uz * vx - ux * vz;
    const cz = ux * vy - uy * vx;
    return Math.sqrt(cx * cx + cy * cy + cz * cz) / 2;
  };
  // The polygon's own sides are the loop's open edges and never chords; a
  // chord is any other (i, j) pair, and it is barred when the mesh already
  // has that edge or the walk visits the same vertex twice.
  const barred = (i: number, j: number): boolean => {
    if (j - i <= 1 || (i === 0 && j === n - 1)) return false;
    const a = loop[i];
    const b = loop[j];
    if (a === b) return true;
    return existingEdges.has(a < b ? a * vertexCount + b : b * vertexCount + a);
  };

  const cost = new Float64Array(n * n);
  const split = new Int32Array(n * n).fill(-1);
  for (let span = 2; span < n; span++) {
    for (let i = 0; i + span < n; i++) {
      const j = i + span;
      let best = Infinity;
      let at = -1;
      if (!barred(i, j)) {
        for (let k = i + 1; k < j; k++) {
          const left = cost[i * n + k];
          const right = cost[k * n + j];
          if (left === Infinity || right === Infinity) continue;
          const total = left + right + areaOf(i, k, j);
          if (total < best) {
            best = total;
            at = k;
          }
        }
      }
      cost[i * n + j] = best;
      split[i * n + j] = at;
    }
  }
  if (cost[0 * n + (n - 1)] === Infinity) return null;

  const triangles: number[] = [];
  const emit = (i: number, j: number): void => {
    const k = split[i * n + j];
    if (k < 0) return;
    // Reversed against the walk: the polygon runs i→k→j, the fill traverses
    // j→k→i so the boundary edges pair with the sheet's own.
    triangles.push(loop[j], loop[k], loop[i]);
    emit(i, k);
    emit(k, j);
  };
  emit(0, n - 1);
  return triangles;
}

/**
 * Strict manifold census: directed edges with no opposite twin, and directed
 * edges used more than once. Raw — whoever calls it subtracts the borders
 * that are open on purpose.
 */
function countUnpairedDirected(geometry: BufferGeometry): {
  unpaired: number;
  sameDirection: number;
} {
  const index = geometry.getIndex()!;
  const vertexCount = geometry.getAttribute('position').count;
  const directed = new Map<number, number>();
  for (let i = 0; i < index.count; i += 3) {
    for (let e = 0; e < 3; e++) {
      const key = index.getX(i + e) * vertexCount + index.getX(i + ((e + 1) % 3));
      directed.set(key, (directed.get(key) ?? 0) + 1);
    }
  }
  let sameDirection = 0;
  let unpaired = 0;
  directed.forEach((count, key) => {
    if (count > 1) sameDirection++;
    const a = Math.floor(key / vertexCount);
    const b = key - a * vertexCount;
    if ((directed.get(b * vertexCount + a) ?? 0) === 0 && count === 1) unpaired++;
  });
  return { unpaired, sameDirection };
}

/** All parts into one indexed geometry (positions only). */
function concatenateParts(parts: BufferGeometry[]): BufferGeometry {
  let totalVerts = 0;
  let totalCorners = 0;
  for (const part of parts) {
    const pos = part.getAttribute('position');
    totalVerts += pos.count;
    totalCorners += part.getIndex()?.count ?? pos.count;
  }
  const positions = new Float32Array(totalVerts * 3);
  const indices = new Uint32Array(totalCorners);
  let vertexBase = 0;
  let cornerBase = 0;
  for (const part of parts) {
    const pos = part.getAttribute('position');
    positions.set(pos.array as Float32Array, vertexBase * 3);
    const index = part.getIndex();
    if (index) {
      for (let i = 0; i < index.count; i++) indices[cornerBase + i] = vertexBase + index.getX(i);
      cornerBase += index.count;
    } else {
      for (let i = 0; i < pos.count; i++) indices[cornerBase + i] = vertexBase + i;
      cornerBase += pos.count;
    }
    vertexBase += pos.count;
  }
  const merged = new BufferGeometry();
  merged.setAttribute('position', new BufferAttribute(positions, 3));
  merged.setIndex(new BufferAttribute(indices, 1));
  return merged;
}

/** Undirected edge use counts keyed by min·V+max. */
function buildEdgeUse(geometry: BufferGeometry): { use: Map<number, number>; vertexCount: number } {
  const index = geometry.getIndex()!;
  const vertexCount = geometry.getAttribute('position').count;
  const use = new Map<number, number>();
  for (let i = 0; i < index.count; i += 3) {
    for (let e = 0; e < 3; e++) {
      const a = index.getX(i + e);
      const b = index.getX(i + ((e + 1) % 3));
      if (a === b) continue;
      const key = a < b ? a * vertexCount + b : b * vertexCount + a;
      use.set(key, (use.get(key) ?? 0) + 1);
    }
  }
  return { use, vertexCount };
}

function countOpenEdges(geometry: BufferGeometry): number {
  const { use } = buildEdgeUse(geometry);
  let open = 0;
  use.forEach((c) => {
    if (c === 1) open++;
  });
  return open;
}

function countSameDirectionPairs(geometry: BufferGeometry): number {
  const index = geometry.getIndex()!;
  const vertexCount = geometry.getAttribute('position').count;
  const directed = new Map<number, number>();
  for (let i = 0; i < index.count; i += 3) {
    for (let e = 0; e < 3; e++) {
      const a = index.getX(i + e);
      const b = index.getX(i + ((e + 1) % 3));
      if (a === b) continue;
      const key = a * vertexCount + b;
      directed.set(key, (directed.get(key) ?? 0) + 1);
    }
  }
  let pairs = 0;
  directed.forEach((count) => {
    if (count > 1) pairs++;
  });
  return pairs;
}

/** Spatial-hash cell for a point (cellSize-quantized, packed to one number). */
function cellKey(x: number, y: number, z: number, cellSize: number): number {
  const ix = Math.max(-1000, Math.min(1000, Math.round(x / cellSize)));
  const iy = Math.max(-1000, Math.min(1000, Math.round(y / cellSize)));
  const iz = Math.max(-1000, Math.min(1000, Math.round(z / cellSize)));
  return ix + 1024 + (iy + 1024) * 2048 + (iz + 1024) * 2048 * 2048;
}

/**
 * Splits border-edge chords at border vertices lying on them (within
 * `tolerance`), re-counting each pass, until convergence or `maxPasses`.
 * Mutates the geometry's index in place; returns the split count.
 */
function splitTJunctions(
  geometry: BufferGeometry,
  tolerance: number,
  maxPasses: number,
  stats: SeamFusionStats,
): number {
  const positionAttr = geometry.getAttribute('position') as BufferAttribute;
  const vertexCount = positionAttr.count;
  const pu = new Vector3();
  const pv = new Vector3();
  const pw = new Vector3();
  const seg = new Vector3();
  const toW = new Vector3();
  const cellSize = Math.max(tolerance, 1e-3);
  const tolSq = tolerance * tolerance;

  let tris: Uint32Array | number[] = geometry.getIndex()!.array as Uint32Array;
  let total = 0;

  for (let pass = 0; pass < maxPasses; pass++) {
    const use = new Map<number, number>();
    for (let i = 0; i < tris.length; i += 3) {
      for (let e = 0; e < 3; e++) {
        const a = tris[i + e];
        const b = tris[i + ((e + 1) % 3)];
        if (a === b) continue;
        const key = a < b ? a * vertexCount + b : b * vertexCount + a;
        use.set(key, (use.get(key) ?? 0) + 1);
      }
    }
    const borderVertex = new Uint8Array(vertexCount);
    const buckets = new Map<number, number[]>();
    use.forEach((count, key) => {
      if (count !== 1) return;
      const a = Math.floor(key / vertexCount);
      const b = key - a * vertexCount;
      for (const v of [a, b]) {
        if (borderVertex[v]) continue;
        borderVertex[v] = 1;
        const cell = cellKey(
          positionAttr.getX(v),
          positionAttr.getY(v),
          positionAttr.getZ(v),
          cellSize,
        );
        let list = buckets.get(cell);
        if (!list) {
          list = [];
          buckets.set(cell, list);
        }
        list.push(v);
      }
    });

    // Split each triangle at most once per pass (multi-vertex chords converge
    // over the following passes).
    const next: number[] = [];
    let splits = 0;
    for (let i = 0; i < tris.length; i += 3) {
      const tri = [tris[i], tris[i + 1], tris[i + 2]];
      let splitAt = -1;
      let splitEdge = 0;
      let bestDistSq = tolSq;
      if (tri[0] !== tri[1] && tri[1] !== tri[2] && tri[2] !== tri[0]) {
        for (let e = 0; e < 3 && splitAt < 0; e++) {
          const u = tri[e];
          const v = tri[(e + 1) % 3];
          const x = tri[(e + 2) % 3];
          const key = u < v ? u * vertexCount + v : v * vertexCount + u;
          if (use.get(key) !== 1) continue;
          pu.fromBufferAttribute(positionAttr, u);
          pv.fromBufferAttribute(positionAttr, v);
          seg.subVectors(pv, pu);
          const lenSq = seg.lengthSq();
          if (lenSq < 1e-12) continue;

          const minX = Math.min(pu.x, pv.x) - tolerance;
          const maxX = Math.max(pu.x, pv.x) + tolerance;
          const minY = Math.min(pu.y, pv.y) - tolerance;
          const maxY = Math.max(pu.y, pv.y) + tolerance;
          const minZ = Math.min(pu.z, pv.z) - tolerance;
          const maxZ = Math.max(pu.z, pv.z) + tolerance;
          for (let cx = Math.round(minX / cellSize); cx <= Math.round(maxX / cellSize); cx++) {
            for (let cy = Math.round(minY / cellSize); cy <= Math.round(maxY / cellSize); cy++) {
              for (let cz = Math.round(minZ / cellSize); cz <= Math.round(maxZ / cellSize); cz++) {
                const list = buckets.get(cellKey(cx * cellSize, cy * cellSize, cz * cellSize, cellSize));
                if (!list) continue;
                for (const w of list) {
                  if (w === u || w === v || w === x) continue;
                  pw.fromBufferAttribute(positionAttr, w);
                  toW.subVectors(pw, pu);
                  const t = toW.dot(seg) / lenSq;
                  if (t < 0.02 || t > 0.98) continue;
                  const distSq = toW.addScaledVector(seg, -t).lengthSq();
                  if (distSq >= bestDistSq) continue;
                  bestDistSq = distSq;
                  splitAt = w;
                  splitEdge = e;
                }
              }
            }
          }
        }
      }
      if (splitAt < 0) {
        next.push(tri[0], tri[1], tri[2]);
      } else {
        const u = tri[splitEdge];
        const v = tri[(splitEdge + 1) % 3];
        const x = tri[(splitEdge + 2) % 3];
        next.push(u, splitAt, x, splitAt, v, x);
        splits++;
      }
    }
    tris = next;
    total += splits;
    stats.passes++;
    if (splits === 0) break;
  }

  geometry.setIndex(tris instanceof Uint32Array ? new BufferAttribute(tris, 1) : tris);
  return total;
}

/**
 * Zips narrow slits shut: mutually-nearest OPPOSING border vertices closer
 * than `maxGap` snap to their midpoint (a later weld merges them). Opposing
 * means: not reachable within 3 steps along the border (not the same shore
 * rounding a corner) and with roughly parallel border tangents. Real windows
 * wider than `maxGap` and the outer perimeter have no partner and stay put.
 * Returns the number of snapped pairs.
 */
function snapOpposingShores(geometry: BufferGeometry, maxGap: number): number {
  const positionAttr = geometry.getAttribute('position') as BufferAttribute;
  const { use, vertexCount } = buildEdgeUse(geometry);

  const neighbours = new Map<number, number[]>();
  use.forEach((count, key) => {
    if (count !== 1) return;
    const a = Math.floor(key / vertexCount);
    const b = key - a * vertexCount;
    if (!neighbours.has(a)) neighbours.set(a, []);
    if (!neighbours.has(b)) neighbours.set(b, []);
    neighbours.get(a)!.push(b);
    neighbours.get(b)!.push(a);
  });
  if (neighbours.size === 0) return 0;

  const cellSize = Math.max(maxGap, 1e-3);
  const buckets = new Map<number, number[]>();
  neighbours.forEach((_n, v) => {
    const cell = cellKey(
      positionAttr.getX(v),
      positionAttr.getY(v),
      positionAttr.getZ(v),
      cellSize,
    );
    let list = buckets.get(cell);
    if (!list) {
      list = [];
      buckets.set(cell, list);
    }
    list.push(v);
  });

  // 3-step border neighbourhood (same shore around a corner must not pair).
  const nearOnBorder = (v: number): Set<number> => {
    const seen = new Set<number>([v]);
    let frontier = [v];
    for (let step = 0; step < 3; step++) {
      const next: number[] = [];
      for (const u of frontier) {
        for (const n of neighbours.get(u) ?? []) {
          if (!seen.has(n)) {
            seen.add(n);
            next.push(n);
          }
        }
      }
      frontier = next;
    }
    return seen;
  };

  const pv = new Vector3();
  const pw = new Vector3();
  const bestOf = new Map<number, number>();
  neighbours.forEach((_n, v) => {
    pv.fromBufferAttribute(positionAttr, v);
    const skip = nearOnBorder(v);
    let best = -1;
    let bestSq = maxGap * maxGap;
    const cx0 = Math.round((pv.x - maxGap) / cellSize);
    const cx1 = Math.round((pv.x + maxGap) / cellSize);
    const cy0 = Math.round((pv.y - maxGap) / cellSize);
    const cy1 = Math.round((pv.y + maxGap) / cellSize);
    const cz0 = Math.round((pv.z - maxGap) / cellSize);
    const cz1 = Math.round((pv.z + maxGap) / cellSize);
    for (let cx = cx0; cx <= cx1; cx++) {
      for (let cy = cy0; cy <= cy1; cy++) {
        for (let cz = cz0; cz <= cz1; cz++) {
          const list = buckets.get(cellKey(cx * cellSize, cy * cellSize, cz * cellSize, cellSize));
          if (!list) continue;
          for (const w of list) {
            if (skip.has(w)) continue;
            pw.fromBufferAttribute(positionAttr, w);
            const dSq = pw.distanceToSquared(pv);
            if (dSq >= bestSq) continue;
            bestSq = dSq;
            best = w;
          }
        }
      }
    }
    if (best >= 0) bestOf.set(v, best);
  });

  let zipped = 0;
  bestOf.forEach((w, v) => {
    if (v >= w) return; // handle each pair once
    if (bestOf.get(w) !== v) return; // mutual nearest only
    const mx = (positionAttr.getX(v) + positionAttr.getX(w)) / 2;
    const my = (positionAttr.getY(v) + positionAttr.getY(w)) / 2;
    const mz = (positionAttr.getZ(v) + positionAttr.getZ(w)) / 2;
    positionAttr.setXYZ(v, mx, my, mz);
    positionAttr.setXYZ(w, mx, my, mz);
    zipped++;
  });
  return zipped;
}
